/**
 * "Why TDC and NextOnVera" — why the initiative sits inside Tour De Cure,
 * and why NextOnVera is the flagship platform the Fund exists to back.
 */

export type WhyPoint = {
  title: string;
  body: string;
};

export type NextOnVeraStrategyPhase = {
  id: string;
  index: string;
  title: string;
  functionCopy: string;
  cancerCoverage: string;
};

export const whyTdcNextOnVeraPage = {
  documentTitle: "Why TDC and NextOnVera · NextOnVera",
  eyebrow: "The vehicle",
  title: "Why TDC and NextOnVera",
  lead: "NextOnVera is not a new charity. In close partnership with and fully supported by Tour de Cure because brain cancer needs a grants engine that already works — and it backs NextOnVera because the next options for patients will come from putting evidence easily into the hands of clinical and research teams.",
  heroPanelImage: "brand/nextonvera-logo.png",
  heroPanelImageAlt: "NextOnVera",
  nextonveraImage: "generated-images/nextonvera-hero.png",
  nextonveraImageAlt: "NextOnVera oncology intelligence — evidence-based treatment matching at the bedside.",
  tdc: {
    eyebrow: "The charity",
    heading: "Why Tour de Cure",
    body: [
      "Tour de Cure has spent twenty years assessing cancer research. Independent review of competitive applications, deductible-gift status, and public reporting already sit behind every dollar.",
      "NextOnVera is not a side petition. Backed by Tour de Cure, it benefits from that proven machinery, so early-stage, high-risk work can move without asking donors to trust an untested organisation."
    ],
    points: [
      {
        title: "Proven grants machinery",
        body: "1,328 cancer research projects funded since 2007, and 280 published research outputs recorded to July 2026. NextOnVera uses that engine; it does not rebuild it."
      },
      {
        title: "Independent and competitive",
        body: "Research grants are awarded through an independent, competitive review process open to any Australian institution, with transparency on what is funded and what is found. The NextOnVera platform is a Tour de Cure-led program funded against milestone gates set by the research committee."
      },
      {
        title: "Built for trust",
        body: "Registered Australian charity, DGR endorsed, milestone-based reporting. Donors give through a pathway that already exists — not a new one invented for a campaign."
      },
      {
        title: "Speed with discipline",
        body: "The Fund can back ideas too early for NHMRC or MRFF without skipping the scientific assessment donors expect. Fast capital. Same review gates."
      }
    ] satisfies WhyPoint[]
  },
  nextonvera: {
    eyebrow: "The platform",
    heading: "Why NextOnVera",
    body: [
      "NextOnVera is a clinical decision-support software platform under validation. It is designed to read a patient's molecular and clinical picture and put cited, structured options in front of the tumour board, for the clinicians to weigh. It is a working prototype, not a research poster, and it will only be used in patient care if validation and regulatory approval show that it should be.",
      "Brain cancer is the proving ground — complex, fragmented, time-critical. The architecture is built to extend to other forms of cancer once the methodology is proven."
    ],
    points: [
      {
        title: "Evidence for the tumour board",
        body: "Structured, cited reports for oncologists, tumour boards and researchers, never a black-box replacement for the people who decide."
      },
      {
        title: "Under validation",
        body: "NextOnVera is entering formal validation with clinician partners. The program is designed so a tumour board always completes its own discussion before any NextOnVera output is seen, and every output is checked against the board's decision. That is how we will learn whether it adds value, and how we earn a place in the room."
      },
      {
        title: "Built for brain cancer first",
        body: "Glioblastoma is where progress has been very limited. NextOnVera is designed around that complexity, then ready to extend pan-cancer."
      },
      {
        title: "Metro intelligence, regional reach",
        body: "Time-poor and regional teams cannot search thousands of disconnected sources before the next treatment decision is made. NextOnVera is built so that, once validated and approved, they have easy access to a richer set of options."
      }
    ] satisfies WhyPoint[]
  },
  breakthroughs: {
    id: "breakthroughs",
    eyebrow: "Tour De Cure record",
    heading: "Breakthroughs",
body: "Every bar is a year of published breakthroughs from Tour De Cure-funded research. The chart covers all 280 dated records from 2010 to 2026. With your support and donations, more breakthroughs can be achieved.",
    source: "Tour De Cure Breakthrough Summary, July 2026",
    xAxisLabel: "Year",
    yAxisLabel: "Breakthroughs"
  },
  strategy: {
    id: "nextonvera-strategy",
    eyebrow: "NextOnVera Strategy",
    heading: "Four phases — function and cancer coverage",
    lead: "NextOnVera is built in four phases. Each one has a job, and each one covers a defined set of cancers. The frames are here; the detail lands as the strategy is locked.",
    phases: [
      {
        id: "phase-1",
        index: "01",
        title: "Phase 1",
        functionCopy: "Function to be confirmed.",
        cancerCoverage: "Cancer coverage to be confirmed."
      },
      {
        id: "phase-2",
        index: "02",
        title: "Phase 2",
        functionCopy: "Function to be confirmed.",
        cancerCoverage: "Cancer coverage to be confirmed."
      },
      {
        id: "phase-3",
        index: "03",
        title: "Phase 3",
        functionCopy: "Function to be confirmed.",
        cancerCoverage: "Cancer coverage to be confirmed."
      },
      {
        id: "phase-4",
        index: "04",
        title: "Phase 4",
        functionCopy: "Function to be confirmed.",
        cancerCoverage: "Cancer coverage to be confirmed."
      }
    ] satisfies NextOnVeraStrategyPhase[]
  },
  cta: {
    eyebrow: "Next step",
    heading: "See the platform, or back the work",
    body: "Read how NextOnVera works, or give to the Brain Cancer Fund — any amount, any donor — so early science and this platform both keep moving.",
    primary: { label: "Explore NextOnVera", href: "/nextonvera", variant: "default" as const },
    secondary: { label: "The Fund", href: "/the-fund", variant: "outline" as const },
    donateLabel: "Donate via Tour de Cure"
  }
} as const;
